import React, { createContext, useContext, useCallback, useState, ReactNode, useEffect } from 'react'
import * as Toast from '@radix-ui/react-toast'
import { MessageSquareIcon, HandshakeIcon, XIcon, TrashIcon } from 'lucide-react'
import '../styles/toast.css'

// Toast context types
interface ToastContextType {
  showToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error' | 'discard') => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

// Individual toast item interface
interface ToastItem {
  id: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error' | 'discard'
  open: boolean
  progress: number
  startTime: number
}

// Toast duration in milliseconds (increased from 5000 to 10000)
const TOAST_DURATION = 10000

// Helper function to get progress bar color based on toast type
const getProgressColor = (type: 'info' | 'success' | 'warning' | 'error' | 'discard') => {
  switch (type) {
    case 'success':
      return '#16a34a'
    case 'warning':
      return '#d97706'
    case 'error':
      return '#dc2626'
    case 'discard':
      return '#2563eb' // Blue for discard
    case 'info':
    default:
      return '#dc2626' // Red to match app badges
  }
}

// Helper function to get appropriate icon based on message content and type
const getToastIcon = (message: string, type: 'info' | 'success' | 'warning' | 'error' | 'discard') => {
  // Check if message is about deals/negocios
  if (message.toLowerCase().includes('negocio')) {
    return <HandshakeIcon className="ToastIcon" />
  }

  // Use type parameter for icon selection
  switch (type) {
    case 'success':
      return <HandshakeIcon className="ToastIcon" />
    case 'discard':
      return <TrashIcon className="ToastIcon" />
    case 'warning':
    case 'error':
    case 'info':
    default:
      return <MessageSquareIcon className="ToastIcon" />
  }
}

// Toast Provider component
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const showToast = useCallback(
    (message: string, type: 'info' | 'success' | 'warning' | 'error' | 'discard' = 'info') => {
      const id = Math.random().toString(36).substr(2, 9)
      const newToast: ToastItem = {
        id,
        message,
        type,
        open: true,
        progress: 100,
        startTime: Date.now(),
      }

      setToasts((prev) => [...prev, newToast])

      // Auto-close toast after 10 seconds
      setTimeout(() => {
        setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, open: false } : t)))
      }, TOAST_DURATION)

      // Remove from state after animation completes
      setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id))
      }, TOAST_DURATION + 500)
    },
    []
  )

  const handleOpenChange = useCallback((open: boolean, toastId: string) => {
    if (!open) {
      setToasts((prev) => prev.map((t) => (t.id === toastId ? { ...t, open: false } : t)))
      // Remove from state after animation
      setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== toastId))
      }, 500)
    }
  }, [])

  // Progress bar update effect - only run when there are active toasts
  useEffect(() => {
    if (toasts.length === 0) return

    const interval = setInterval(() => {
      setToasts((prev) => {
        const hasActiveToasts = prev.some((toast) => toast.open)

        if (!hasActiveToasts) return prev

        return prev.map((toast) => {
          if (!toast.open) return toast

          const elapsed = Date.now() - toast.startTime
          const progress = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100)

          return { ...toast, progress }
        })
      })
    }, 100) // Reduced frequency to 100ms for better performance

    return () => clearInterval(interval)
  }, [toasts.length]) // Only re-run when number of toasts changes

  return (
    <ToastContext.Provider value={{ showToast }}>
      <Toast.Provider duration={TOAST_DURATION} swipeDirection="right">
        {children}

        {toasts.map((toast) => (
          <Toast.Root
            key={toast.id}
            className="ToastRoot"
            data-type={toast.type}
            open={toast.open}
            onOpenChange={(open) => handleOpenChange(open, toast.id)}
          >
            <div className="ToastMain">
              <div className="ToastContent">
                {getToastIcon(toast.message, toast.type)}
                <Toast.Description className="ToastDescription">{toast.message}</Toast.Description>
              </div>

              <Toast.Close className="ToastClose" aria-label="Cerrar">
                <XIcon className="h-4 w-4" />
              </Toast.Close>
            </div>

            {/* Reverse Progress Bar */}
            <div className="ToastProgressContainer">
              <div
                className="ToastProgressBar"
                style={{
                  width: `${toast.progress}%`,
                  backgroundColor: getProgressColor(toast.type),
                }}
              />
            </div>
          </Toast.Root>
        ))}

        <Toast.Viewport className="ToastViewport" />
      </Toast.Provider>
    </ToastContext.Provider>
  )
}

// Custom hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext)

  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }

  return context
}
